import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  API_BASE_URL,
  canjearInvitacion,
  loginDesarrollo,
  obtenerCuentaActual,
  obtenerInvitacionPublica,
  obtenerMetodosLogin,
  RolAcceso,
} from "../api";
import AvatarMascota from "../componentes/AvatarMascota";

const NOMBRES_ROL: Record<number, { titulo: string; icono: string; descripcion: string }> = {
  [RolAcceso.Veterinario]: {
    titulo: "Veterinario",
    icono: "🩺",
    descripcion: "Podrás registrar atenciones, recetas, exámenes clínicos y ver el historial completo.",
  },
  [RolAcceso.Cuidador]: {
    titulo: "Cuidador",
    icono: "🐾",
    descripcion: "Podrás consultar las fechas de vacunas, desparasitaciones y recordatorios diarios.",
  },
  [RolAcceso.Lector]: {
    titulo: "Lector (Solo ver)",
    icono: "📖",
    descripcion: "Acceso para ver la información de la mascota sin realizar modificaciones.",
  },
  [RolAcceso.CoDueno]: {
    titulo: "Co-dueño",
    icono: "🤝",
    descripcion: "Acceso de co-propietario a la ficha de la mascota.",
  },
};

export default function CanjeInvitacion() {
  const { token } = useParams<{ token: string }>();
  const navegar = useNavigate();
  const qc = useQueryClient();

  const [errorCanje, setErrorCanje] = useState<string | null>(null);

  const sesion = useQuery({
    queryKey: ["cuenta"],
    queryFn: obtenerCuentaActual,
  });

  const invitacion = useQuery({
    queryKey: ["invitacion-publica", token],
    queryFn: () => obtenerInvitacionPublica(token!),
    enabled: Boolean(token),
  });

  const metodosLogin = useQuery({
    queryKey: ["metodos"],
    queryFn: obtenerMetodosLogin,
  });

  const mutacionCanjear = useMutation({
    mutationFn: () => canjearInvitacion(token!),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["mascotas"] });
      qc.invalidateQueries({ queryKey: ["mascota", res.mascotaID] });
      navegar(`/mascotas/${res.mascotaID}`);
    },
    onError: (err) => {
      setErrorCanje(err instanceof Error ? err.message : "No se pudo canjear la invitación.");
    },
  });

  if (invitacion.isPending) {
    return (
      <main className="shell">
        <p className="muted">Verificando invitación…</p>
      </main>
    );
  }

  if (invitacion.error || !invitacion.data) {
    return (
      <main className="shell">
        <section className="panel" style={{ textAlign: "center", padding: "2.5rem 1.5rem" }}>
          <span style={{ fontSize: "3rem" }}>⚠️</span>
          <h2>Invitación no encontrada</h2>
          <p className="muted" style={{ maxWidth: "28rem", margin: "0.5rem auto 1.5rem" }}>
            El enlace de invitación no es válido o ha sido cancelado por el propietario.
          </p>
          <Link to="/" className="boton-primario">
            Ir a Mis Mascotas
          </Link>
        </section>
      </main>
    );
  }

  const inv = invitacion.data;
  const rolInfo = NOMBRES_ROL[inv.rolOtorgado] ?? {
    titulo: "Invitado",
    icono: "🐾",
    descripcion: "Acceso a la ficha clínica.",
  };

  const usuario = sesion.data;

  return (
    <main className="shell canje-invitacion-shell">
      <div className="canje-invitacion-tarjeta panel">
        <header className="canje-cabecera">
          <AvatarMascota
            nombre={inv.nombreMascota}
            especie={inv.especie}
            fotoPerfilUrl={inv.fotoPerfilUrl}
            tamano="xl"
          />
          <div className="canje-info-mascota">
            <span className="etiqueta neutra">{inv.especie}</span>
            <h1 style={{ margin: "0.25rem 0 0" }}>{inv.nombreMascota}</h1>
            <p className="muted" style={{ margin: "0.25rem 0 0" }}>
              Te han invitado a acceder a su ficha clínica
            </p>
          </div>
        </header>

        {!inv.esValida ? (
          <div className="alerta-error" style={{ margin: "1.5rem 0" }}>
            <strong>Invitación no disponible</strong>
            <p style={{ margin: "0.25rem 0 0" }}>
              {inv.motivoInvalidez || "Esta invitación ya no es válida."}
            </p>
          </div>
        ) : (
          <>
            <section className="canje-detalles-permisos">
              <h3>Condiciones del acceso:</h3>
              <div className="permiso-item">
                <span className="permiso-icono">{rolInfo.icono}</span>
                <div>
                  <strong>Rol: {rolInfo.titulo}</strong>
                  <p className="muted" style={{ margin: 0 }}>
                    {rolInfo.descripcion}
                  </p>
                </div>
              </div>

              <div className="permiso-item">
                <span className="permiso-icono">{inv.puedeEditar ? "✏️" : "👁️"}</span>
                <div>
                  <strong>
                    Permisos: {inv.puedeEditar ? "Lectura y edición" : "Solo lectura"}
                  </strong>
                  <p className="muted" style={{ margin: 0 }}>
                    {inv.puedeEditar
                      ? "Podrás registrar atenciones, vacunas, recetas y adjuntos."
                      : "Podrás ver el historial clínico sin realizar cambios."}
                  </p>
                </div>
              </div>

              <div className="permiso-item">
                <span className="permiso-icono">⏳</span>
                <div>
                  <strong>
                    Vigencia:{" "}
                    {inv.duracionAccesoDias > 0
                      ? `${inv.duracionAccesoDias} días`
                      : "Tiempo indefinido"}
                  </strong>
                  <p className="muted" style={{ margin: 0 }}>
                    El acceso comenzará inmediatamente una vez que aceptes la invitación.
                  </p>
                </div>
              </div>
            </section>

            {errorCanje && <p className="err">{errorCanje}</p>}

            <footer className="canje-acciones">
              {usuario ? (
                <div style={{ textAlign: "center", width: "100%" }}>
                  <p className="muted" style={{ marginBottom: "0.75rem" }}>
                    Conectado como <strong>{usuario.nombre}</strong> ({usuario.email})
                  </p>
                  <button
                    type="button"
                    className="boton-primario"
                    style={{ width: "100%", fontSize: "1.05rem", padding: "0.75rem" }}
                    disabled={mutacionCanjear.isPending}
                    onClick={() => mutacionCanjear.mutate()}
                  >
                    {mutacionCanjear.isPending
                      ? "Aceptando invitación…"
                      : `✓ Aceptar invitación y ver ficha de ${inv.nombreMascota}`}
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: "center", width: "100%" }}>
                  <p className="muted" style={{ marginBottom: "1rem" }}>
                    Inicia sesión en tu cuenta para vincular la ficha clínica de {inv.nombreMascota}:
                  </p>

                  {metodosLogin.data?.google && (
                    <a
                      className="boton-primario"
                      style={{ display: "block", marginBottom: "0.75rem", textAlign: "center" }}
                      href={`${API_BASE_URL}/login/google?volverA=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                    >
                      Iniciar sesión con Google
                    </a>
                  )}

                  {metodosLogin.data?.desarrollo && (
                    <LoginRapidoDesarrollo onLoginExitoso={() => mutacionCanjear.mutate()} />
                  )}
                </div>
              )}
            </footer>
          </>
        )}
      </div>
    </main>
  );
}

function LoginRapidoDesarrollo({ onLoginExitoso }: { onLoginExitoso: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("veterinario@fichas.local");

  const entrar = useMutation({
    mutationFn: () => loginDesarrollo(email),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cuenta"] });
      onLoginExitoso();
    },
  });

  return (
    <div className="dev" style={{ textAlign: "left", marginTop: "1rem" }}>
      <h4>Login de desarrollo (Simular veterinario / usuario)</h4>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          entrar.mutate();
        }}
        style={{ display: "flex", gap: "0.5rem" }}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{ flex: 1 }}
        />
        <button type="submit" className="boton-primario" disabled={entrar.isPending}>
          Entrar y Aceptar
        </button>
      </form>
      {entrar.error && <p className="err">{entrar.error.message}</p>}
    </div>
  );
}
